import React, { useContext, useState } from "react";
import { IoColorPaletteOutline, IoLanguage, IoMoonOutline, IoSunnyOutline } from "react-icons/io5";
import { MdOutlineContrast } from "react-icons/md";
import { AiFillSound } from "react-icons/ai";
import AppContext from "../../../context/AppContext";
import translations from "../../common/Translations";
import CommonButton from "../../common/CommonButton";

export const GeneralSetting = () => {
  const {
    toggleTheme,
    theme,
    langMode,
    toggleLangMode,
  } = useContext(AppContext);

  const [notificationVolume, setNotificationVolume] = useState(() => {
    return parseInt(localStorage.getItem("notificationVolume")) || 50;
  });

  const handleVolumeChange = (e) => {
    const volume = parseInt(e.target.value);
    setNotificationVolume(volume);
    localStorage.setItem("notificationVolume", volume);
  };

  const playDefaultSound = () => {
    const audio = new Audio("/sound/notification.mp3");
    audio.volume = notificationVolume / 100;
    audio.play();
  };

  const sectionLabelClass =
    theme === "dark" ? "text-gray-300" : "text-gray-500";

  const getPillClass = (isActive, extraClass = "") => {
    const base = `inline-flex h-9 items-center justify-center gap-2 rounded-lg border px-2 text-sm font-semibold transition-all duration-200 ease-in-out shadow-sm cursor-pointer ${extraClass}`;
    if (isActive) {
      return `${base} border-[#236DB4] bg-[#236DB4] text-white`;
    }
    return `${base} border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600`;
  };

  const textColorClass =
    theme === "dark"
      ? "text-white"
      : theme === "high-contrast"
        ? "text-black"
        : "text-gray-900";

  return (
    <div className={`w-full flex flex-col gap-9 ${textColorClass}`}>
      {/* Notification Sound Section */}
      <div>
        <h3 className={`text-xs font-bold uppercase tracking-[0.14em] ${sectionLabelClass}`}>
          {translations[langMode].NotificationSound}
        </h3>
        <div className="mt-2 border-t border-gray-300/80 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700">
              <AiFillSound className="h-5 w-5" />
            </span>
            <input
              type="range"
              min="0"
              max="100"
              value={notificationVolume}
              onChange={handleVolumeChange}
              className="w-60"
            />
            <CommonButton colorIntent="primary" type="button" onClick={playDefaultSound} variant="primary">
              {translations[langMode].TestSound}
            </CommonButton>
          </div>
        </div>
      </div>

      {/* Appearance Section */}
      <div>
        <h3 className={`text-xs font-bold uppercase tracking-[0.14em] ${sectionLabelClass}`}>
          Appearance
        </h3>
        <div className="mt-2 border-t border-gray-300/80 pt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700">
                <IoColorPaletteOutline className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">{translations[langMode].colormode}</p>
                <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                  Choose your display theme
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group">
              <button
                type="button"
                onClick={() => toggleTheme("light")}
                className={getPillClass(theme === "light", "w-[100px]")}
              >
                <IoSunnyOutline className="h-4 w-4" />
                {translations[langMode].light}
              </button>
              <button
                type="button"
                onClick={() => toggleTheme("dark")}
                className={getPillClass(theme === "dark", "w-[100px]")}
              >
                <IoMoonOutline className="h-4 w-4" />
                {translations[langMode].dark}
              </button>
              <button
                type="button"
                onClick={() => toggleTheme("high-contrast")}
                className={getPillClass(theme === "high-contrast", "w-[100px]")}
              >
                <MdOutlineContrast className="h-4 w-4" />
                HC
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Section */}
      <div>
        <h3 className={`text-xs font-bold uppercase tracking-[0.14em] ${sectionLabelClass}`}>
          Regional
        </h3>
        <div className="mt-2 border-t border-gray-300/80 pt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700">
                <IoLanguage className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">{translations[langMode].lang}</p>
                <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                  App display language
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group">
              <button
                type="button"
                onClick={() => toggleLangMode("en")}
                className={getPillClass(langMode === "en", "w-[100px]")}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => toggleLangMode("ur")}
                className={getPillClass(langMode === "ur", "w-[100px]")}
              >
                UR
              </button>
              <button
                type="button"
                onClick={() => toggleLangMode("ar")}
                className={getPillClass(langMode === "ar", "w-[100px]")}
              >
                AR
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
